import { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button as MuiButton, CircularProgress, TextField } from '@mui/material';
import Modal from '../atoms/modal/modal';
import Button from '../atoms/button';
import { FetchImageFromBackoffice } from '../../utils/requests';
import {
  describeVinLookupError,
  lookupVehicleByVin,
  VinLookupMatch,
} from '../../api/vehicles';
import theme from '../../theme';

export type EditedVehicleInformation = {
  vin: string;
  make: string;
  model: string;
  year?: number;
  variant?: string;
  variantId?: string;
};

type EditVehicleInformationModalProps = {
  open: boolean;
  onClose: () => void;
  onSaved?: (saved: EditedVehicleInformation, exactMatch?: boolean) => void;
  vin?: string | null;
  make?: string | null;
  model?: string | null;
  year?: number | null;
  imageId?: string | null;
  vehicleVin?: string | null;
  workshopId?: string;
  workSessionId?: string;
};

const uniqueModels = (matches: VinLookupMatch[]) => {
  const map = new Map<string, VinLookupMatch>();
  for (const match of matches) {
    const modelId = String(match.modelId);
    if (!map.has(modelId)) {
      map.set(modelId, match);
    }
  }
  return Array.from(map.values());
};

const variantName = (match: VinLookupMatch) => String(match.variant ?? '').trim();

const uniqueVariantsForModel = (matches: VinLookupMatch[], modelId: string) => {
  const map = new Map<string, VinLookupMatch>();
  for (const match of matches) {
    if (String(match.modelId) !== modelId) {
      continue;
    }
    const name = variantName(match);
    if (!map.has(name)) {
      map.set(name, match);
    }
  }
  return Array.from(map.values());
};

const EditVehicleInformationModal = ({
  open,
  onClose,
  onSaved,
  vin,
  make,
  model,
  year,
  imageId,
  vehicleVin,
  workshopId,
  workSessionId,
}: EditVehicleInformationModalProps) => {
  const [vinValue, setVinValue] = useState('');
  const [makeValue, setMakeValue] = useState('');
  const [modelValue, setModelValue] = useState('');
  const [yearValue, setYearValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [imageUrl, setImageUrl] = useState<string>();
  const [imageLoading, setImageLoading] = useState(false);
  const [lookupMatches, setLookupMatches] = useState<VinLookupMatch[]>([]);
  const [selectedModelId, setSelectedModelId] = useState('');
  const [selectedVariantName, setSelectedVariantName] = useState('');
  const [selectedVariantId, setSelectedVariantId] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);

  const modelOptions = useMemo(() => uniqueModels(lookupMatches), [lookupMatches]);
  const variantOptions = useMemo(
    () => (selectedModelId ? uniqueVariantsForModel(lookupMatches, selectedModelId) : []),
    [lookupMatches, selectedModelId]
  );
  const selectedVariant = variantOptions.find((match) => variantName(match) === selectedVariantName);
  const hasResolvedLookup = lookupMatches.length > 0;
  
  // Check if this is an exact match: 1 model and 1 variant
  const isExactMatch = useMemo(() => {
    return modelOptions.length === 1 && variantOptions.length === 1 && selectedVariantName !== '';
  }, [modelOptions.length, variantOptions.length, selectedVariantName]);

  useEffect(() => {
    if (open) {
      setVinValue(vin ?? '');
      setMakeValue(make ?? '');
      setModelValue(model ?? '');
      setYearValue(year != null ? String(year) : '');
      setError(null);
      setLookupMatches([]);
      setSelectedModelId('');
      setSelectedVariantName('');
      setSelectedVariantId('');
    }
  }, [open, vin, make, model, year]);

  const resetResolvedFields = (nextMake = '', nextModel = '', nextYear: string = '') => {
    setMakeValue(nextMake);
    setModelValue(nextModel);
    setYearValue(nextYear);
    setLookupMatches([]);
    setSelectedModelId('');
    setSelectedVariantName('');
    setSelectedVariantId('');
  };

  const applyVariantSelection = (match: VinLookupMatch) => {
    setSelectedVariantName(variantName(match));
    setSelectedVariantId(match.variantId ?? '');
    setMakeValue(match.make);
    setModelValue(match.model);
    setYearValue(String(match.estimatedManufacturingYear));
  };

  const applyModelSelection = (modelId: string, matches: VinLookupMatch[]) => {
    const modelMatch = uniqueModels(matches).find((match) => String(match.modelId) === modelId);
    if (!modelMatch) {
      return;
    }

    setSelectedModelId(modelId);
    setMakeValue(modelMatch.make);
    setModelValue(modelMatch.model);

    const nextVariants = uniqueVariantsForModel(matches, modelId);
    if (nextVariants.length === 1) {
      applyVariantSelection(nextVariants[0]);
      return;
    }

    setSelectedVariantName('');
    setYearValue('');
  };

  const handleVinChange = (nextVin: string) => {
    setVinValue(nextVin);
    if (lookupMatches.length > 0) {
      resetResolvedFields(make ?? '', model ?? '', year != null ? String(year) : '');
    }
  };

  const handleLookup = async () => {
    const trimmedVin = vinValue.trim();
    setError(null);
    setLookupLoading(true);

    try {
      const data = await lookupVehicleByVin(trimmedVin);
      const matches = data?.matches ?? [];
      if (matches.length === 0) {
        resetResolvedFields(make ?? '', model ?? '', year != null ? String(year) : '');
        setError('The VIN could not be verified. Edit the VIN and try Look up VIN again.');
        return;
      }

      setLookupMatches(matches);

      const models = uniqueModels(matches);
      if (models.length === 1) {
        applyModelSelection(String(models[0].modelId), matches);
        return;
      }

      const uniqueMakeIds = new Set(models.map((match) => String(match.makeId)));
      setMakeValue(uniqueMakeIds.size === 1 ? models[0].make : '');
      setModelValue('');
      setSelectedModelId('');
      setSelectedVariantName('');
      setYearValue('');
    } catch (lookupError) {
      console.error(lookupError);
      resetResolvedFields(make ?? '', model ?? '', year != null ? String(year) : '');
      setError(describeVinLookupError(
        lookupError,
        'The VIN could not be verified. Edit the VIN and try Look up VIN again.'
      ));
    } finally {
      setLookupLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !imageId || !vehicleVin) {
      setImageUrl(undefined);
      return;
    }

    let cancelled = false;
    let objectUrl: string | undefined;

    const fetchImage = async () => {
      setImageLoading(true);
      setImageUrl(undefined);
      try {
        const blob = await FetchImageFromBackoffice<Blob>(
          `portal/files/${vehicleVin}/files/${imageId}`
        );
        objectUrl = URL.createObjectURL(blob);
        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        setImageUrl(objectUrl);
      } catch (fetchError) {
        console.warn('Inspection image fetch failed:', fetchError);
        if (!cancelled) {
          setImageUrl(undefined);
        }
      } finally {
        if (!cancelled) {
          setImageLoading(false);
        }
      }
    };

    fetchImage();

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [open, imageId, vehicleVin]);

  const handleSave = () => {
    const trimmedVin = vinValue.trim();
    const trimmedMake = makeValue.trim();
    const trimmedModel = modelValue.trim();
    const trimmedYear = yearValue.trim();

    onSaved?.(
      {
        vin: trimmedVin,
        make: trimmedMake,
        model: trimmedModel,
        year: trimmedYear ? Number(trimmedYear) : undefined,
        variant: selectedVariantName || undefined,
        variantId: selectedVariantId || undefined,
      },
      isExactMatch
    );
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit vehicle information"
      size="large"
      footer={
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            onClick={onClose}
            extend={{
              color: theme.colors.grey,
              fontSize: 16,
              padding: '8px',
            }}
          >
            CANCEL
          </Button>

          <Button
            onClick={handleSave}
            extend={{
              color: theme.colors.primary,
              fontSize: 16,
              padding: '8px',
            }}
          >
            SAVE
          </Button>
        </Box>
      }
    >
      {error && <Alert severity="error">{error}</Alert>}
      {hasResolvedLookup && !error && (
        <Alert
          severity="info"
          sx={{
            mb: 2,
            bgcolor: theme.colors.primaryLight,
            color: theme.colors.text,
            '& .MuiAlert-icon': { color: theme.colors.primary },
          }}
        >
          {isExactMatch 
            ? 'VIN lookup found an exact match. Click SAVE to update the vehicle information.'
            : 'Select the correct model and year from the options returned by the VIN lookup to continue.'
          }
        </Alert>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 3, alignItems: 'flex-start' }}>
        {imageId && vehicleVin ? (
          <Box sx={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {imageLoading ? (
              <CircularProgress size={40} />
            ) : imageUrl ? (
              <img
                src={imageUrl}
                alt=""
                style={{ display: 'block', maxWidth: '60vw', maxHeight: '80vh' }}
              />
            ) : null}
          </Box>
        ) : null}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minWidth: 240 }}>
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
              <TextField
                label="VIN"
                value={vinValue}
                onChange={(event) => handleVinChange(event.target.value)}
                size="small"
                fullWidth
                autoFocus
                disabled={lookupLoading}
              />
              <MuiButton
                variant="outlined"
                onClick={handleLookup}
                size="small"
                disabled={lookupLoading || !vinValue.trim()}
                sx={{
                  height: 40,
                  minWidth: 132,
                  whiteSpace: 'nowrap',
                  color: theme.colors.primary,
                  borderColor: theme.colors.primary,
                  '&:hover': {
                    borderColor: theme.colors.primary,
                    backgroundColor: `${theme.colors.primary}20`,
                  },
                  '&:disabled': {
                    color: theme.colors.grey,
                    borderColor: theme.colors.grey,
                  },
                }}
              >
                {lookupLoading ? <CircularProgress size={20} /> : 'Look up VIN'}
              </MuiButton>
            </Box>
          </Box>
          <TextField
            label="Make"
            value={makeValue}
            onChange={(event) => setMakeValue(event.target.value)}
            size="small"
            fullWidth
            disabled={hasResolvedLookup}
            slotProps={hasResolvedLookup ? { htmlInput: { readOnly: true } } : undefined}
          />
          <TextField
            label="Model"
            value={modelValue}
            onChange={(event) => setModelValue(event.target.value)}
            size="small"
            fullWidth
            disabled={hasResolvedLookup}
            slotProps={hasResolvedLookup ? { htmlInput: { readOnly: true } } : undefined}
          />
          <TextField
            label="Year"
            type="number"
            value={yearValue}
            onChange={(event) => setYearValue(event.target.value)}
            size="small"
            fullWidth
            disabled={hasResolvedLookup}
            slotProps={hasResolvedLookup ? { htmlInput: { readOnly: true } } : undefined}
          />
        </Box>
      </Box>
    </Modal>
  );
};

export default EditVehicleInformationModal;
